import { axiosClient } from '../../../library/Service/axiosClient';

export interface InstituteSearchCriteria {
    instituteGroupId?: string;
    instituteId?: string;
    clientType?: string;
    instituteTypeCode?: string;
}

export interface InstituteRow {
    instituteId: number;
    instituteName: string;
    cityCode: string | null;
    instituteGroupId: string | null;
    instituteTypeCode: string | null;
    clientType: string | null;
    totalAlumni: number;
    activeAlumni: number;
    nonActiveAlumni: number;
    burnersDone: number;
    activeWithBurnerPct: number;
    nonActiveWithBurnerPct: number;
    lastBurnerRun: string | null;
    cooldownDaysRemaining: number;
    isRunAllowed: boolean;
}

export interface InstituteSearchResponse {
    institutes: InstituteRow[];
    summary: { instituteCount: number; totalAlumni: number; burnersDone: number };
    cooldownDays: number;
}

export interface InstituteHeader {
    instituteId: number;
    instituteName: string;
    cityCode: string | null;
    instituteGroupId: string | null;
    instituteTypeCode: string | null;
    clientType: string | null;
    totalAlumni: number;
    activeAlumni: number;
    nonActiveAlumni: number;
    burnersDone: number;
    activeDone: number;
    nonActiveDone: number;
    activeWithBurnerPct: number;
    nonActiveWithBurnerPct: number;
    lastBurnerRun: string | null;
    cooldownDaysRemaining: number;
    isRunAllowed: boolean;
}

export type ToggleValue = '' | 'yes' | 'no';
export type RangeValue = '' | '1m' | '3m' | '6m';

export interface AlumniFilters {
    search?: string;
    programLevel?: string;
    passOutYear?: string | number;
    linkedinExists?: ToggleValue;
    profilePictureExists?: ToggleValue;
    profileCompletionRanges?: string[];
    outreachExists?: ToggleValue;
    outreachRange?: RangeValue;
    engagementExists?: ToggleValue;
    engagementRange?: RangeValue;
    limit?: number;
    offset?: number;
}

export interface AlumniRow {
    alumniId: string;
    alumniName: string;
    email: string;
    mobileCountryCode: string | null;
    mobileNumber: string | null;
    programName: string | null;
    classCode: string | null;
    passOutYear: number | null;
    profileCompletionPercentage: number | null;
    userStatusCode: string | null;
    hasProfilePicture: number;
    hasLinkedin: number | null;
    lastOutreachAt: string | null;
    lastEngagementAt: string | null;
    burnerScores: null | {
        almaMaterPride: string | null;
        tooltipAlmaMaterPride: string | null;
        marketabilityQuotient: string | null;
        tooltipMarketability: string | null;
        vibrancyLevel: string | null;
        tooltipVibrancy: string | null;
        donor: number | null;
        tooltipDonor: string | null;
        mentor: number | null;
        tooltipMentor: string | null;
        recruiter: number | null;
        tooltipRecruiter: string | null;
    };
    dataSources: null | { pi: boolean; pm: boolean; el: boolean; ol: boolean; sm: boolean };
    lastAnalysisAt: string | null;
    cooldownDaysRemaining: number;
    isRunAllowed: boolean;
}

export interface AlumniSearchResponse {
    alumni: AlumniRow[];
    total: number;
    cooldownDays: number;
}

class BurnerAnalysisService {
    static baseUrl(): string {
        return `${process.env.REACT_APP_REPORTS_SERVER}/burner-analysis`;
    }

    static defaultSearchCriteria(): InstituteSearchCriteria {
        return {
            instituteGroupId: '',
            instituteId: '',
            clientType: 'CLIENT',
            instituteTypeCode: '',
        };
    }

    static emptySearchCriteria(): InstituteSearchCriteria {
        return {
            instituteGroupId: '',
            instituteId: '',
            clientType: '',
            instituteTypeCode: '',
        };
    }

    static searchInstitutes(criteria: InstituteSearchCriteria) {
        return axiosClient().post<InstituteSearchResponse>(`${this.baseUrl()}/search-institutes`, criteria);
    }

    static triggerInstituteRun(instituteId: number | string) {
        return axiosClient().post(`${this.baseUrl()}/run-institute/${instituteId}`, {});
    }

    static getInstituteHeader(instituteId: number | string) {
        return axiosClient().get<InstituteHeader>(`${this.baseUrl()}/institute/${instituteId}`);
    }

    static getCompletionYears(instituteId: number | string) {
        return axiosClient().get<number[]>(`${this.baseUrl()}/institute/${instituteId}/completion-years`);
    }

    static getProgramLevels(instituteId: number | string) {
        return axiosClient().get<string[]>(`${this.baseUrl()}/institute/${instituteId}/program-levels`);
    }

    static searchInstituteAlumni(instituteId: number | string, filters: AlumniFilters) {
        return axiosClient().post<AlumniSearchResponse>(`${this.baseUrl()}/institute/${instituteId}/alumni`, filters);
    }

    static triggerAlumniRun(alumniId: string, instituteId: number | string) {
        return axiosClient().post(`${this.baseUrl()}/run-alumni/${alumniId}`, { instituteId });
    }

    static defaultAlumniFilters(): AlumniFilters {
        return {
            search: '',
            programLevel: '',
            passOutYear: '',
            linkedinExists: '',
            profilePictureExists: '',
            profileCompletionRanges: [],
            outreachExists: '',
            outreachRange: '',
            engagementExists: '',
            engagementRange: '',
            limit: 50,
            offset: 0,
        };
    }

    static async alumniPageLoader(params: any) {
        const instituteId = params.instituteId;
        const filters = this.defaultAlumniFilters();
        const [header, alumni, years, levels] = await Promise.all([
            this.getInstituteHeader(instituteId),
            this.searchInstituteAlumni(instituteId, filters),
            this.getCompletionYears(instituteId),
            this.getProgramLevels(instituteId),
        ]);
        return {
            formConfig: {},
            data: {
                instituteId,
                header: header.data,
                alumniList: alumni.data.alumni ?? [],
                totalAlumni: alumni.data.total ?? 0,
                completionYears: years.data ?? [],
                programLevels: levels.data ?? [],
                filters,
            },
            internal: {},
            routeInfo: params,
        };
    }

    static async pageLoader(params: any) {
        const criteria = this.defaultSearchCriteria();
        const search = await this.searchInstitutes(criteria);
        return {
            formConfig: {},
            data: {
                searchCriteria: criteria,
                instituteList: search.data.institutes ?? [],
                summary: search.data.summary ?? { instituteCount: 0, totalAlumni: 0, burnersDone: 0 },
            },
            internal: {},
            routeInfo: params,
        };
    }
}

export default BurnerAnalysisService;
